import { useEffect } from 'react'
import RulesSectionBody from './RulesSectionBody'
import RulesSectionNav from './RulesSectionNav'
import { RULES_SECTIONS, rulesLastUpdated, type RulesSectionId } from './content'
import {
  rulesSectionElementId,
  scrollToRulesSection,
  useRulesScrollSpy,
} from './useRulesScrollSpy'
import './rules.css'

/**
 * The canonical rules destination.
 *
 * A read-only page with no CTAs and no accordions: people arrive scanning for one thing, and an
 * accordion hides exactly the thing they came for. Everything is expanded, the nav moves you, and
 * the visuals do the explaining.
 *
 * There is no URL router in this app, so the brief's `#banker` deep link is `initialSection` —
 * scrolled to on mount without animation, because a page that scrolls itself while you are reading
 * the top of it is worse than one that just starts in the right place.
 */
export default function HowToPlayScreen({
  initialSection,
  onBack,
}: {
  initialSection?: RulesSectionId | null
  onBack: () => void
}) {
  const activeId = useRulesScrollSpy()

  useEffect(() => {
    if (!initialSection) return
    scrollToRulesSection(initialSection, false)
  }, [initialSection])

  const lastUpdated = new Date(rulesLastUpdated()).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className="how-to-play">
      <div className="how-to-play__header">
        <button
          type="button"
          className="how-to-play__back"
          onClick={onBack}
          aria-label="Back"
        >
          ← Back
        </button>
        <p className="how-to-play__title">How to play</p>
      </div>

      <RulesSectionNav
        activeId={activeId}
        onSelect={(id) => scrollToRulesSection(id, true)}
      />

      <div className="how-to-play__sections">
        {RULES_SECTIONS.map((section) => (
          <section
            key={section.id}
            id={rulesSectionElementId(section.id)}
            className="rules-section"
          >
            <RulesSectionBody section={section} />
          </section>
        ))}
      </div>

      <p className="how-to-play__updated">Last updated {lastUpdated}</p>
    </div>
  )
}
